/**
 * @file typography-container.component.tsx
 * @description Контейнер для отображения типографически оформленного содержимого с изолированными стилями, 
 * имитирующий оформление Quill Editor. Поддерживает темную тему, форматирование заголовков, списков, ссылок
 * и обеспечивает консистентную типографику независимо от глобальных стилей приложения.
 * @author Genotroid
 * @created 2025-09-03
 * @updated 2025-09-03
 * @dependencies [react]
 * @related-files []
 */

import type { FC, ReactNode } from 'react';

/**
 * Props для компонента типографического контейнера
 */
interface ITypographyContainerProps {
    /** Дочерние элементы для рендеринга внутри контейнера */
    children: ReactNode;
    /** Включить темную тему оформления */
    darkMode?: boolean;
    /** Обработчик клика по контейнеру */
    onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
}

export const TypographyContainer: FC<ITypographyContainerProps> = ({ children, darkMode = false, ...props }) => {
    return (
        <div {...props} className={`typography-container ${darkMode ? 'dark' : ''}`}>
            <div className="ql-container">
                <div className="ql-editor">{children}</div>
            </div>
            <style>{`
                /* Контейнер, имитирующий рабочую область Quill */
                .ql-container {
                    box-sizing: border-box;
                    font-family: 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif;
                    font-size: 1.37566vmin;
                    margin: 0;
                    box-shadow: 0 0 0.5291vmin rgba(0, 0, 0, 0.1);
                }
                .ql-editor {
                    box-sizing: border-box;
                    line-height: 1.42;
                    overflow-y: auto;
                }

                .typography-container .ql-editor {
                  padding: 0
                }
                /* Типографика для заголовков внутри редактора */
                .ql-editor h1,
                .ql-editor h2,
                .ql-editor h3,
                .ql-editor h4,
                .ql-editor h5,
                .ql-editor h6 {
                    margin-bottom: 0.5em;
                    font-weight: bold;
                    font-family: system-ui, sans-serif;
                }
                .ql-editor h1 {
                    font-size: 2em;
                }
                .ql-editor h2 {
                    font-size: 1.75em;
                }
                .ql-editor h3 {
                    font-size: 1.5em;
                }
                .ql-editor h4 {
                    font-size: 1.25em;
                }
                .ql-editor h5 {
                    font-size: 1em;
                }
                .ql-editor h6 {
                    font-size: 0.875em;
                }
                /* Типографика для списков */
                .ql-editor ul,
                .ql-editor ol {
                    margin: 1em 0;
                    padding-left: 1.5em;
                }
                .ql-editor ul {
                    list-style: disc;
                }
                .ql-editor ol {
                    list-style: decimal;
                }
                .ql-editor a {
                    color: var(--color-typo-link);
                    text-decoration: underline;
                }
                .ql-editor a:hover {
                    color: var(--color-typo-link-hover);
                }
            `}</style>
        </div>
    );
};
